/**
 * React Query hooks for the Inbox (SPEC E1c — unified-inbox, R4).
 *
 * Poll-on-focus (no websockets): every query has `staleTime: 0` +
 * `refetchOnWindowFocus: true`, so switching back to the tab refreshes the inbox
 * — the v1 real-time substitute.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  listInbox,
  listContactConversations,
  getConversation,
  getContact,
  getContactOpportunities,
  getPendingDraft,
  sendMessage,
  addInternalNote,
} from '../inbox';
import type {
  InboxListResponse,
  ContactConversationsResponse,
  ConversationDetail,
  ContactDetail,
  ContactOpportunity,
  PendingDraft,
} from '@/types/inbox';

export const inboxKeys = {
  all: ['inbox'] as const,
  list: (params?: Record<string, unknown>) => [...inboxKeys.all, 'list', params] as const,
  contactConversations: (contactId: string) =>
    [...inboxKeys.all, 'contact-conversations', contactId] as const,
  conversation: (conversationId: string) =>
    [...inboxKeys.all, 'conversation', conversationId] as const,
  contact: (contactId: string) => [...inboxKeys.all, 'contact', contactId] as const,
  opportunities: (contactId: string) =>
    [...inboxKeys.all, 'opportunities', contactId] as const,
  pendingDraft: (conversationId: string) =>
    [...inboxKeys.all, 'pending-draft', conversationId] as const,
};

export function useInbox(params: {
  channel?: string;
  status?: string;
  assignee_id?: string;
} = {}): UseQueryResult<InboxListResponse, Error> {
  return useQuery({
    queryKey: inboxKeys.list(params),
    queryFn: () => listInbox(params),
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

export function useContactConversations(
  contactId: string
): UseQueryResult<ContactConversationsResponse, Error> {
  return useQuery({
    queryKey: inboxKeys.contactConversations(contactId),
    queryFn: () => listContactConversations(contactId),
    enabled: !!contactId,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

export function useConversation(
  conversationId: string
): UseQueryResult<ConversationDetail, Error> {
  return useQuery({
    queryKey: inboxKeys.conversation(conversationId),
    queryFn: () => getConversation(conversationId),
    enabled: !!conversationId,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

export function useContact(
  contactId: string
): UseQueryResult<ContactDetail, Error> {
  return useQuery({
    queryKey: inboxKeys.contact(contactId),
    queryFn: () => getContact(contactId),
    enabled: !!contactId,
  });
}

export function useContactOpportunities(
  contactId: string
): UseQueryResult<ContactOpportunity[], Error> {
  return useQuery({
    queryKey: inboxKeys.opportunities(contactId),
    queryFn: () => getContactOpportunities(contactId),
    enabled: !!contactId,
  });
}

export function usePendingDraft(
  conversationId: string
): UseQueryResult<PendingDraft | null, Error> {
  return useQuery({
    queryKey: inboxKeys.pendingDraft(conversationId),
    queryFn: () => getPendingDraft(conversationId),
    enabled: !!conversationId,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

/** DIRECT operator send via the channel adapter (R1). Invalidates the thread +
 * inbox list on success. */
export function useSendMessage(): UseMutationResult<
  { message_id: string; conversation_id: string; channel: string },
  Error,
  { conversationId: string; content: string },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, content }) => sendMessage(conversationId, content),
    onSuccess: (_data, { conversationId }) => {
      queryClient.invalidateQueries({ queryKey: inboxKeys.conversation(conversationId) });
      queryClient.invalidateQueries({ queryKey: inboxKeys.all });
    },
  });
}

/** Add an internal note (NOT channel-sent). */
export function useAddNote(): UseMutationResult<
  { message_id: string; conversation_id: string; direction: string },
  Error,
  { conversationId: string; content: string },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, content }) => addInternalNote(conversationId, content),
    onSuccess: (_data, { conversationId }) => {
      queryClient.invalidateQueries({ queryKey: inboxKeys.conversation(conversationId) });
    },
  });
}
